// Subir un archivo genérico (PDF/imagen) a la ficha de un paciente como
// DocumentReference (Rediseño mayor 2026-09-02, Fase 2 de "destinos
// reales del nav" — "Subir resultado"). Mismo mecanismo EXACTO que la
// firma escaneada de MotorFormularioConsentimiento.tsx/
// CapturaFirmaPaciente.tsx (FileReader → Binary → DocumentReference), acá
// sin el paso de firma — un documento suelto (resultado de labo, imagen,
// informe externo), visible automático en DocumentosTab.tsx (que solo
// filtra por `patient=`, sin filtro de `type`).
//
// Vínculo con la orden (2026-09-11, pedido explícito) — el ServiceRequest de
// una solicitud de laboratorio/imagenología (LabsTab.tsx) se creaba SIEMPRE
// en `active` y nada en el sistema lo pasaba a `completed` cuando llegaba el
// resultado (quedaba "active" para siempre salvo edición manual en Medplum).
// Ahora, al subir un archivo, se puede elegir a qué orden ACTIVA del
// paciente corresponde: el DocumentReference queda enlazado vía
// `context.related` (DocumentReference no tiene `basedOn` en FHIR R4) y, si
// se eligió una, esa ServiceRequest pasa a `completed`. Elegir "documento
// suelto" mantiene el comportamiento de siempre (sin vínculo, para
// informes externos que no vienen de una orden hecha acá).
import type { DocumentReference, Reference, Patient, ServiceRequest } from '@medplum/fhirtypes';
import { useMedplum, useSearchResources } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LoteTransaccional, recursoRespuesta } from './discretos/transaccionFhir';

const SIN_ORDEN = '__ninguna__';

const TAMANO_MAXIMO_BYTES = 10 * 1024 * 1024; // 10MB — no existía ningún tope antes para uploads a Medplum en hce-chart, se agrega acá.
const TIPOS_ACEPTADOS = 'image/*,application/pdf';

export interface SubirDocumentoProps {
  readonly subject: Reference<Patient>;
  readonly onSubido: (docRef: DocumentReference) => void;
}

function leerArchivoComoDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el archivo.'));
    reader.readAsDataURL(file);
  });
}

function dataUrlABytes(dataUrl: string, contentTypeFallback: string): { bytes: Uint8Array; contentType: string } {
  const match = /^data:([^;]*);base64,(.*)$/.exec(dataUrl);
  const contentType = match?.[1] || contentTypeFallback;
  const b64 = match ? match[2] : dataUrl;
  const binario = atob(b64);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  return { bytes, contentType };
}

export function SubirDocumento({ subject, onSubido }: SubirDocumentoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ordenElegidaId, setOrdenElegidaId] = useState(SIN_ORDEN);
  const [ordenesActivas] = useSearchResources('ServiceRequest', { patient: subject.reference ?? '', status: 'active', _sort: '-authored', _count: 100 });
  const listaOrdenes = (ordenesActivas ?? []) as ServiceRequest[];

  function elegirArchivo(file: File | null): void {
    setError(null);
    if (file && file.size > TAMANO_MAXIMO_BYTES) {
      setArchivo(null);
      setError(t('subirDocumento.archivoDemasiadoGrande'));
      return;
    }
    setArchivo(file);
  }

  async function confirmar(): Promise<void> {
    if (!archivo) return;
    setSubiendo(true);
    setError(null);
    try {
      const orden = ordenElegidaId === SIN_ORDEN ? undefined : listaOrdenes.find((o) => o.id === ordenElegidaId);
      const dataUrl = await leerArchivoComoDataUrl(archivo);
      const { bytes, contentType } = dataUrlABytes(dataUrl, archivo.type || 'application/octet-stream');
      const binario = await medplum.createBinary({ data: bytes, contentType, filename: archivo.name, securityContext: subject.reference ? { reference: subject.reference } : undefined }); // MT-042: el paciente, para la regla de vínculo asistencial
      // El documento y el paso de la orden a "completed" van en UNA transacción (el Binary ya se subió antes).
      const lote = new LoteTransaccional();
      const docLocal = lote.crear<DocumentReference>({
        resourceType: 'DocumentReference',
        status: 'current',
        subject,
        type: { text: orden?.code?.text ?? t('subirDocumento.tipoDocumento') },
        date: new Date().toISOString(),
        context: orden?.id ? { related: [{ reference: `ServiceRequest/${orden.id}`, display: orden.code?.text }] } : undefined,
        content: [{ attachment: { contentType: binario.contentType, url: `Binary/${binario.id}`, title: archivo.name } }],
      });
      // La orden pasa a "completed" recién cuando llega SU resultado — antes
      // quedaba "active" para siempre, ver cabecera del archivo.
      if (orden) lote.actualizar<ServiceRequest & { id: string }>({ ...(orden as ServiceRequest & { id: string }), status: 'completed' });
      const respuesta = await lote.enviar(medplum);
      const docRef = recursoRespuesta(respuesta, 0, docLocal);
      setArchivo(null);
      setOrdenElegidaId(SIN_ORDEN);
      onSubido(docRef);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
    setSubiendo(false);
  }

  return (
    <div className="flex flex-col gap-2">
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div>
        <label className="block text-xs font-mono uppercase tracking-wide text-hce-muted mb-1">{t('subirDocumento.ordenLabel')}</label>
        <Select value={ordenElegidaId} onValueChange={setOrdenElegidaId}>
          <SelectTrigger>
            <SelectValue placeholder={t('subirDocumento.ordenPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SIN_ORDEN}>{t('subirDocumento.ordenNinguna')}</SelectItem>
            {listaOrdenes.map((o) => (
              <SelectItem key={o.id} value={o.id ?? ''}>
                {o.code?.text ?? o.category?.[0]?.text ?? o.id}
                {o.authoredOn ? ` (${new Date(o.authoredOn).toLocaleDateString('es-EC')})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept={TIPOS_ACEPTADOS}
          aria-label={t('subirDocumento.elegirArchivo')}
          className="hidden"
          onChange={(e) => elegirArchivo(e.target.files?.[0] ?? null)}
        />
        <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
          {t('subirDocumento.elegirArchivo')}
        </Button>
        {archivo && <span className="truncate text-sm text-hce-ink">{archivo.name}</span>}
      </div>
      <div className="flex justify-end">
        <Button type="button" size="sm" disabled={!archivo || subiendo} onClick={() => void confirmar()}>
          {subiendo ? t('subirDocumento.subiendo') : t('subirDocumento.subir')}
        </Button>
      </div>
    </div>
  );
}
